import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { Carousel } from 'components/Carousels'
import styles from './TestimonialFullBackgroundCarousel.module.scss'
import { Typography, MediaImageStandard } from 'components/Contentful'
import { Grid, Box } from '@achieve/ascend'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import SlideOnVisible from 'components/SlideOnVisible'
import { SLIDE_ON_VISIBLE_EASING_DEFAULT } from 'constants/animation'
import classNames from 'classnames'
import { getSlug } from 'utils/conversions'

function Card({ item: { cardImage, cardTitleText, cardText }, priority }) {
  // we are using anything smaller than lg as our mobile for this one
  // for now though, we will make a medium screen resolution later
  // *** UPDATE 7/8/22 ***
  // The lg breakpoint was changed (now 1024px) and xl (1280) is closer to the old lg (1200).
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const [quote] = cardText?.json?.content ?? []
  return (
    <Grid
      container
      direction={{ xs: 'column-reverse', lg: 'row' }}
      justifyContent={{ xs: 'flex-end', lg: 'space-between' }}
      alignItems={{ xs: 'center', lg: 'flex-start' }}
    >
      {isMobile ? (
        <>
          <Grid size={{ xs: 12 }} className="basis-full">
            <Grid size={{ xs: 12 }} className={classNames(styles['text-container'], 'basis-full')}>
              <Grid container direction="row" className={styles['card-text-grid']}>
                <Typography
                  className={classNames(styles['carousel-item-quote'], 'm-auto')}
                  content={quote}
                  fontWeight="regular"
                  variant="bodySm"
                  data-testid="testimonial-carousel-item-quote"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} className="basis-full">
            <div className={classNames(styles['image-container'], 'm-auto')}>
              <span>
                <MediaImageStandard
                  content={cardImage}
                  fetchPriority="high"
                  preload={priority}
                  className={styles['image']}
                />
              </span>
            </div>
          </Grid>
          <Grid size={{ xs: 12 }} className={classNames(styles['text-container'], 'basis-full')}>
            <Grid container direction="row" className={styles['card-text-grid']}>
              <Grid size={{ xs: 12 }} className="basis-full">
                <Typography
                  className={classNames(
                    styles['carousel-item-title'],
                    'displayMd lg:displayLg font-bold! m-auto'
                  )}
                  content={cardTitleText}
                  fontWeight="bold"
                  data-testid="testimonial-carousel-item-title"
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      ) : (
        <>
          <Grid size={{ xs: 12, lg: 3 }} className=" lg:basis-3/12">
            <SlideOnVisible
              scrollOnMobile={false}
              duration={1200}
              timeout={300}
              easing={'cubic-bezier(.90,1,.90,.5)'}
            >
              <div className={styles['image-container']}>
                <span>
                  <MediaImageStandard
                    content={cardImage}
                    fetchPriority="high"
                    preload={priority}
                    className={styles['image']}
                  />
                </span>
              </div>
            </SlideOnVisible>
          </Grid>
          <Grid
            size={{ xs: 12, lg: 8 }}
            className={classNames(styles['text-container'], 'lg:basis-8/12')}
          >
            <Grid container direction="row" className={styles['card-text-grid']}>
              <SlideOnVisible
                scrollOnMobile={false}
                duration={800}
                timeout={100}
                easing={SLIDE_ON_VISIBLE_EASING_DEFAULT}
              >
                <Grid size={{ xs: 12 }} className="basis-full">
                  <Typography
                    className={classNames(
                      styles['carousel-item-title'],
                      'displayMd lg:displayLg font-bold!'
                    )}
                    content={cardTitleText}
                    fontWeight="bold"
                    data-testid="testimonial-carousel-item-title"
                  />
                </Grid>
              </SlideOnVisible>
              <SlideOnVisible
                scrollOnMobile={false}
                duration={900}
                timeout={0}
                easing={SLIDE_ON_VISIBLE_EASING_DEFAULT}
              >
                <Grid size={{ xs: 12 }} className="basis-full">
                  <Typography
                    className={styles['carousel-item-quote']}
                    content={quote}
                    fontWeight="regular"
                    variant="bodySm"
                    data-testid="testimonial-carousel-item-quote"
                  />
                </Grid>
              </SlideOnVisible>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  )
}

function TestimonialFullBackgroundCarousel({ content }) {
  let memoizedContent = useMemoizedContentGetter(content, ['carousel', 'backgroundConfigJson'])
  const isMobile = useViewportSmallerThan(BREAKPOINTS.md)

  const {
    carousel: { carouselItemsCollection },
    backgroundConfigJson: { jsonContent },
  } = memoizedContent
  const backgroundObjectPosition = jsonContent?.backgroundObjectPosition ?? 'right'
  const backgroundObjectPositionMobile = jsonContent?.backgroundObjectPositionMobile ?? 'bottom'
  return (
    <Section
      className={styles['section']}
      contain={false}
      data-narrow={true}
      backgroundObjectPositionClass={`bg-${getSlug(backgroundObjectPositionMobile)} lg:bg-${getSlug(
        backgroundObjectPosition
      )}`}
      showPattern={jsonContent?.showPattern}
      variant={jsonContent?.background ?? 'bgDefault'}
    >
      <Box className={styles['fullbg-content']} component="div">
        <Carousel
          className={styles['flat-carousel-wrapper']}
          carouselItems={carouselItemsCollection?.items || []}
          itemComponent={Card}
          withIndicators={isMobile}
          variant="standard"
          idPrefix="full-bg-testimonial-carousel"
        />
      </Box>
    </Section>
  )
}
export { TestimonialFullBackgroundCarousel }

export default TestimonialFullBackgroundCarousel
